import {
    JOB_DATA,
    JobName,
    LEVEL_ITEMS,
    MATERIA_LEVEL_MAX_NORMAL,
    MATERIA_LEVEL_MAX_OVERMELD,
    MATERIA_SLOTS_MAX,
    MateriaSubstat,
    statById,
    SupportedLevel
} from "@xivgear/xivmath/xivconstants";
import {
    DisplayGearSlotInfo,
    DisplayGearSlotKey,
    DisplayGearSlotMapping,
    EquipSlotKey,
    EquipSlotMap,
    EquipSlots,
    EquipSlotValue,
    FoodItem,
    GearAcquisitionSource,
    GearItem,
    IlvlSyncInfo, ItemQuality,
    JobMultipliers,
    Materia,
    MateriaSlot,
    MedicineItem,
    OccGearSlotKey,
    RawStatKey,
    RawStats,
    RawStatsPart,
    RelicStatModel
} from "@xivgear/xivmath/geartypes";
import {getRelicStatModelFor} from "./relicstats/relicstats";
import {requireArrayTyped, requireNumber, requireString} from "./external/data_validators";
import {
    DataApiClient,
    EquipSlotCategory,
    GearAcquisitionSource as AcqSrc,
    SpecialStatType
} from "@xivgear/data-api-client/dataapi";
import {BaseParamMap, DataManager, DmJobs} from "./datamanager";
import {applyStatCaps} from "./gear";
import {toTranslatable, TranslatableString} from "@xivgear/i18n/translation";
import {
    ApiFoodData,
    ApiItemData,
    ApiMateriaData,
    ApiMedicineData,
    checkResponse,
    DATA_API_CLIENT
} from "./data_api_client";
import {addStats} from "@xivgear/xivmath/xivstats";
import {arrayEqTyped} from "@xivgear/util/array_utils";
import {xivApiIconUrl} from "./external/xivapi";
import {statCapNoJob, statCapWithJob} from "@xivgear/xivmath/xivmath";

export class NewApiDataManager implements DataManager {

    private readonly _minIlvl: number;
    private readonly _maxIlvl: number;
    private readonly _minIlvlFood: number;
    private readonly _maxIlvlFood: number;
    private readonly _classJob: JobName;
    private readonly _allJobs: JobName[];
    private readonly _level: SupportedLevel;
    private readonly _specificItems: Set<number>;
    private readonly _ilvlSync: number | undefined;
    private readonly apiClient: DataApiClient<never>;
    private readonly minEquipIlvl: number;
    private readonly minEquipLvl: SupportedLevel;

    public constructor(classJobs: DmJobs, level: SupportedLevel, ilvlSync?: number | undefined, specificItems?: Set<number>) {
        this._classJob = classJobs[0];
        this._allJobs = [...classJobs];
        this._level = level;
        const lvlData = LEVEL_ITEMS[this._level];
        this._minIlvl = lvlData.minILvl;
        this._maxIlvl = lvlData.maxILvl;
        this._minIlvlFood = lvlData.minILvlFood;
        this._maxIlvlFood = lvlData.maxILvlFood;
        this._ilvlSync = ilvlSync;
        this._specificItems = specificItems;
        this.apiClient = DATA_API_CLIENT;
        this.minEquipLvl = Math.min(...classJobs.map(job => JOB_DATA[job].minLevel)) as SupportedLevel;
        this.minEquipIlvl = LEVEL_ITEMS[this.minEquipLvl].minILvl;
    }

    private _allItems: DataApiGearInfo[] | undefined;
    private _allMateria: Materia[] | undefined;
    private _allFoodItems: DataApiFoodInfo[] | undefined;
    private _allMedicineItems: DataApiMedicineInfo[] | undefined;
    private _jobMultipliers: Map<JobName, JobMultipliers> | undefined;
    /**
     * _baseParams maps stat keys to BaseParamInfo. BaseParamInfo is a map from slot to percentages and meld params.
     * Meld params are used to calculate substats for that particular job.
     *
     * @private
     */
    private _baseParams: BaseParamMap | undefined;
    private _isyncPromise: Promise<Map<number, IlvlSyncInfo>> | undefined;
    private _isyncData: Map<number, IlvlSyncInfo> | undefined;
    private _maxIlvlForEquipLevel: Map<number, number> | undefined;
    private _maxIlvlForEquipLevelWeapon: Map<number, number> | undefined;

    private queryBaseParams() {
        return this.apiClient.baseParams.baseParams();
    }

    async getIlvlSyncData(baseParamPromise: ReturnType<typeof this.queryBaseParams>, ilvl: number) {
        if (this._isyncPromise === undefined) {
            this._isyncPromise = Promise.all([baseParamPromise, this.apiClient.itemLevel.itemLevels()]).then(responses => {
                const outMap = new Map<number, IlvlSyncInfo>();
                // Iterate over rows in ItemLevel table
                for (const row of checkResponse(responses[1]).data!.items!) {
                    const ilvl = row.rowId!;
                    // Unroll the ItemLevel object into a direct mapping from RawStatKey => modifier
                    // BaseParam data is trickier. First, we need to convert from a list to a map, where the keys are the stat.
                    const baseParams = this._baseParams!;
                    const getIlvlModifier = (statsKey: RawStatKey): number | undefined => {
                        switch (statsKey) {
                            case "hp":
                                return row.HP;
                            case "vitality":
                                return row.vitality;
                            case "strength":
                                return row.strength;
                            case "dexterity":
                                return row.dexterity;
                            case "intelligence":
                                return row.intelligence;
                            case "mind":
                                return row.mind;
                            case "piety":
                                return row.piety;
                            case "crit":
                                return row.criticalHit;
                            case "dhit":
                                return row.directHitRate;
                            case "determination":
                                return row.determination;
                            case "tenacity":
                                return row.tenacity;
                            case "spellspeed":
                                return row.spellSpeed;
                            case "skillspeed":
                                return row.skillSpeed;
                            case "wdPhys":
                                return row.physicalDamage;
                            case "wdMag":
                                return row.magicalDamage;
                            case "weaponDelay":
                                return row.delay;
                            case "defensePhys":
                                return row.defense;
                            case "defenseMag":
                                return row.magicDefense;
                            case "gearHaste":
                                // Don't bother capping haste since it doesn't work like a normal stat.
                                return null;
                            case "extraMainStat":
                                // Main stats should all be the same, but this uses mind like the cap calculation does.
                                return row.mind;
                            case "extraSecondaryStat":
                                // Secondary stats should all be the same, but this uses direct hit like the cap calculation does.
                                return row.directHitRate;
                            case "cp":
                                return row.CP;
                            case "control":
                                return row.control;
                            case "craftsmanship":
                                return row.craftsmanship;
                            case "gp":
                                return row.GP;
                            case "gathering":
                                return row.gathering;
                            case "perception":
                                return row.perception;
                            default:
                                console.warn(`Bad ilvl modifier! Stat: ${statsKey}`);
                                return null;
                        }
                    };
                    outMap.set(ilvl, {
                        ilvl: ilvl,
                        getIlvlModifier,
                        substatCap(slot: OccGearSlotKey, statsKey: RawStatKey, meldParamIndex: number | null): number | null {
                            const ilvlModifier = getIlvlModifier(statsKey);

                            function calcCap(slot: OccGearSlotKey): number | null {
                                if (ilvlModifier === null || ilvlModifier === 0) {
                                    return null;
                                }
                                const bpInfo = baseParams[statsKey as RawStatKey];
                                if (bpInfo === undefined) {
                                    return null;
                                }
                                const baseParamSlotModifier: number = bpInfo.slots[slot];
                                const meldParam = meldParamIndex === null ? 100 : bpInfo.meldParam[meldParamIndex];
                                if (meldParam !== undefined && ilvlModifier !== undefined) {
                                    return statCapWithJob(meldParam, ilvlModifier, baseParamSlotModifier);
                                }
                                else {
                                    return statCapNoJob(ilvlModifier, baseParamSlotModifier);
                                }
                            }

                            // There is some weird rounding behavior that I think I have mostly figured out, but not
                            // completely. There are still some cases where it is off by one.
                            // Here's how I think it works, using Crit on PLD as an example:
                            // BaseParam Critical Hit has 1H% = 100, OH% = 40, and 2H% = 140. Notice how 100 + 40 = 140.
                            // The caps *should* be the same as a 1H, but due to roundoff, they might not be.
                            // In order to prevent stacking roundoff error (i.e. where both round up or both round down),
                            // It seems that you're supposed to just ignore the OH% entirely, and instead calculate the
                            // 2H cap and 1H cap and subtract them.
                            // However, this is not the case for all stats - e.g. Craftsmanship is OH% == 1h% == 2H% = 350.
                            // Gathering stats (excl. GP) are also weird, they have 350 for 2H, but then 350/200 or 200/350.
                            if (slot === 'OffHand' && ilvlModifier !== 0) {
                                const bpInfo = baseParams[statsKey as RawStatKey];
                                if (bpInfo !== undefined) {
                                    const oneCap: number = bpInfo.slots['Weapon1H'];
                                    const twoCap: number = bpInfo.slots['Weapon2H'];
                                    const ohCap: number = bpInfo.slots['OffHand'];
                                    // Combat stats = 1H + OH == 2H
                                    if (oneCap + ohCap === twoCap) {
                                        const h2 = calcCap('Weapon2H');
                                        const h1 = calcCap('Weapon1H');
                                        if (h2 === null || h1 === null) {
                                            return null;
                                        }
                                        return h2 - h1;
                                    }
                                    // Other stats - just go with OH cap
                                }
                            }
                            return calcCap(slot);
                        },
                    } as const);

                }
                this._isyncData = outMap;
                return outMap;
            });
        }
        const data = await this._isyncPromise;
        if (data.has(ilvl)) {
            return data.get(ilvl);
        }
        else {
            console.error(`ilvl ${ilvl} not found`);
            return null;
        }
    }

    itemById(id: number, forceNq: boolean = false): (GearItem | undefined) {
        // @x-ts-expect-error - assumed that DataManager is not meaningfully used prior to loading data
        return this._allItems.find(item => {
            return item.id === id && item.isNqVersion === forceNq;
        });
    }

    materiaById(id: number): (Materia | undefined) {
        if (id < 0) {
            return undefined;
        }
        // @x-ts-expect-error - assumed that DataManager is not meaningfully used prior to loading data
        return this._allMateria.find(item => item.id === id);
    }

    foodById(id: number) {
        // @x-ts-expect-error - assumed that DataManager is not meaningfully used prior to loading data
        return this._allFoodItems.find(food => food.id === id);
    }

    medicineById(id: number) {
        // @x-ts-expect-error - assumed that DataManager is not meaningfully used prior to loading data
        return this._allMedicineItems.find(medicine => medicine.id === id);
    }


    async loadData() {
        const baseParamPromise = this.queryBaseParams().then(response => {
            checkResponse(response);
            this._baseParams = response.data.items!.reduce<BaseParamMap>((baseParams, value) => {
                // Each individual item also gets converted
                baseParams[statById(value.rowId)] = {
                    meldParam: requireArrayTyped(value.meldParam, 'number'),
                    // This maps our internal stat keys to the xivapi percentages.
                    slots: {
                        Body: requireNumber(value.chestPercent),
                        Ears: requireNumber(value.earringPercent),
                        Feet: requireNumber(value.feetPercent),
                        Hand: requireNumber(value.handsPercent),
                        Head: requireNumber(value.headPercent),
                        Legs: requireNumber(value.legsPercent),
                        Neck: requireNumber(value.necklacePercent),
                        OffHand: requireNumber(value.offHandPercent),
                        Ring: requireNumber(value.ringPercent),
                        Weapon2H: requireNumber(value.twoHandWeaponPercent),
                        Weapon1H: requireNumber(value.oneHandWeaponPercent),
                        Wrist: requireNumber(value.braceletPercent),
                        ChestHead: requireNumber(value.chestHeadPercent),
                        ChestHeadLegsFeet: requireNumber(value.chestHeadLegsFeetPercent),
                        ChestLegsFeet: requireNumber(value.chestLegsFeetPercent),
                        ChestLegsGloves: requireNumber(value.chestLegsGlovesPercent),
                        HeadChestHandsLegsFeet: requireNumber(value.headChestHandsLegsFeetPercent),
                        LegsFeet: requireNumber(value.legsFeetPercent),
                    },
                };
                return baseParams;
            }, {});
            return response;
        });
        const extraPromises: Promise<unknown>[] = [];

        console.log("Loading items");
        const itemsQuery: Parameters<typeof this.apiClient.items.items>[0] = {job: this._allJobs};
        if (this._specificItems !== undefined) {
            itemsQuery.itemIds = Array.from(this._specificItems.values());
        }
        const itemsPromise = this.apiClient.items.items(itemsQuery)
            .then(async (response) => {
                checkResponse(response);
                console.log(`Got ${response.data.items.length} Items`);
                return response.data;
            }).then((data) => {
                const rawItems = data.items;
                this._allItems = rawItems
                    .filter(i => {
                        try {
                            // Always include FSH offhand
                            if (i.classJobs.includes('FSH') && i.equipSlotCategory.offHand === 1) {
                                return true;
                            }
                            // Filter out lower-level items for jobs that don't care about it
                            return i.ilvl >= this.minEquipIlvl
                                && (Object.keys(i.baseParamMapHQ).length > 0
                                    // Don't filter out BLU weapons
                                    || (i.classJobs.includes('BLU') && i.equipSlotCategory.mainHand === 1));
                        }
                        catch (e) {
                            console.log(e);
                            throw e;
                        }
                    })
                    .flatMap(i => {
                        if (i.canBeHq) {
                            return [new DataApiGearInfo(i), new DataApiGearInfo(i, true)];
                        }
                        else {
                            return [new DataApiGearInfo(i)];
                        }
                    })
                    .filter(i => {
                        // Filter out ARR/HW junk items
                        if (i.isCustomRelic) {
                            return true;
                        }
                        if (i.equipLvl < this.minEquipLvl) {
                            // Always include weapons.
                            if (i.displayGearSlotName === 'Weapon') {
                                return true;
                            }
                            if (i.displayGearSlotName === 'OffHand' && i.usableByJob('FSH')) {
                                // Include FSH offhand
                                console.log(`Including FSH offhand ${i.id}`);
                                return true;
                            }
                            if (i.materiaSlots.length === 0 && !i.primarySubstat) {
                                return false;
                            }
                            // The item must have some main stat, or a FSH offhand
                            return i.stats.extraMainStat || i.stats.vitality || i.stats.intelligence || i.stats.mind || i.stats.strength || i.stats.dexterity;
                        }
                        return true;

                    });
                const maxIlvlMap = this._maxIlvlForEquipLevel = new Map();
                const maxIlvlWpnMap = this._maxIlvlForEquipLevelWeapon = new Map();
                for (let i = 0; i < data.maxIlvlPerLvl.length; i++) {
                    maxIlvlMap.set(i, data.maxIlvlPerLvl[i]);
                }
                for (let i = 0; i < data.maxIlvlPerLvlWpn.length; i++) {
                    maxIlvlWpnMap.set(i, data.maxIlvlPerLvlWpn[i]);
                }
            });
        const statsPromise = Promise.all([itemsPromise, baseParamPromise]).then(() => {
            console.log(`Finishing item calculations for ${this._allItems.length} items`);
            this._allItems.forEach(item => {
                // console.log(`Item ${item.id} ${item.name}`);
                const itemIlvlPromise = this.getIlvlSyncData(baseParamPromise, item.ilvl);
                let isyncLvl: number | null;
                // Downsync by ilvl directly
                if (this._ilvlSync && this._ilvlSync < item.ilvl) {
                    isyncLvl = this._ilvlSync;
                }
                // If no explicit ilvl sync is specified, but the sheet level is less than the item's level, then
                // sync to the max ilvl for a gear piece of that level. This theoretically corresponds to
                // ParamGrow.ItemLevelSync.
                else if (this._level < item.equipLvl) { // Downsync by equip lvl, infer correct ilvl
                    isyncLvl = this._maxIlvlForEquipLevel.get(this._level);
                }
                else {
                    isyncLvl = null;
                }
                const ilvlSyncPromise: Promise<IlvlSyncInfo> = isyncLvl === null ? Promise.resolve(undefined) : this.getIlvlSyncData(baseParamPromise, isyncLvl);
                extraPromises.push(Promise.all([itemIlvlPromise, ilvlSyncPromise]).then(([native, sync]) => {
                    item.applyIlvlData(native, sync, this._level);
                    if (item.isCustomRelic) {
                        // console.debug('Applying relic model');
                        item.relicStatModel = getRelicStatModelFor(item, this._baseParams, this._classJob);
                        // console.debug('Applied', item.relicStatModel);
                    }
                }));
            });
        });

        // Materia
        console.log("Loading materia");
        const materiaPromise = this.apiClient.materia.materia()
            .then((response) => {
                checkResponse(response);
                console.log(`Got ${response.data.items.length} Materia Types`);
                this._allMateria = response.data.items
                    // TODO: if a materia is discontinued but should still be available for old
                    // sets, this will not work.
                    .filter(i => i.value[MATERIA_LEVEL_MAX_NORMAL - 1])
                    .flatMap(item => {
                        return processRawMateriaInfo(item);
                    });
                console.log(`Processed ${this._allMateria.length} total Materia items`);
            });
        console.log("Loading food");
        const foodPromise = this.apiClient.food.foodItems()
            .then((response) => {
                checkResponse(response);
                console.log(`Got ${response.data.items.length} Food Items`);
                return response.data.items;
            })
            .then((rawFoods) => {
                return rawFoods.map(item => {
                    // TODO: API types prefixing so they don't collide
                    return new DataApiFoodInfo(item);
                });
            })
            .then((processedFoods) => processedFoods.filter(food => Object.keys(food.bonuses).length > 1))
            .then((foods) => this._allFoodItems = foods);
        const medicinePromise = this.apiClient.medicine.medicineItems()
            .then((response) => {
                checkResponse(response);
                console.log(`Got ${response.data.items.length} Medicine Items`);
                return response.data.items;
            })
            .then(rawMedicine => rawMedicine.map(item => new DataApiMedicineInfo(item)))
            .then(medicine => this._allMedicineItems = medicine);
        console.log("Loading jobs");
        const jobsPromise = this.apiClient.jobs.jobs()
            .then(response => {
                checkResponse(response);
                console.log(`Got ${response.data.items.length} Jobs`);
                return response.data.items;
            })
            .then(rawJobs => {
                this._jobMultipliers = new Map<JobName, JobMultipliers>();
                for (const rawJob of rawJobs) {
                    this._jobMultipliers.set(rawJob.abbreviation as JobName, {
                        dexterity: rawJob.modifierDexterity,
                        intelligence: rawJob.modifierIntelligence,
                        mind: rawJob.modifierMind,
                        strength: rawJob.modifierStrength,
                        vitality: rawJob.modifierVitality,
                        hp: rawJob.modifierHitPoints,
                    });
                }
            });
        // These will all resolve at the same time, so it doesn't matter which one we await
        const ilvlPromise = this.getIlvlSyncData(baseParamPromise, 710);
        await Promise.all([baseParamPromise, itemsPromise, statsPromise, materiaPromise, foodPromise, medicinePromise, jobsPromise, ilvlPromise]);
        await Promise.all(extraPromises);
    }

    multipliersForJob(job: JobName): JobMultipliers {
        if (!this._jobMultipliers) {
            throw Error("You must wait for loadData() before calling this method");
        }
        const multi = this._jobMultipliers.get(job);
        if (!multi) {
            throw Error(`No data for job ${job}`);
        }
        return multi;
    }

    get allItems(): DataApiGearInfo[] {
        return this._allItems;
    }

    get allFoodItems(): DataApiFoodInfo[] {
        return this._allFoodItems;
    }

    get allMedicineItems(): DataApiMedicineInfo[] {
        return this._allMedicineItems;
    }

    get allMateria(): Materia[] {
        return this._allMateria;
    }

    get baseParams(): BaseParamMap {
        return this._baseParams;
    }

    get minIlvl(): number {
        return this._minIlvl;
    }

    get maxIlvl(): number {
        return this._maxIlvl;
    }

    get minIlvlFood(): number {
        return this._minIlvlFood;
    }

    get maxIlvlFood(): number {
        return this._maxIlvlFood;
    }

    get ilvlSync(): number | undefined {
        return this._ilvlSync;
    }

    get level(): SupportedLevel {
        return this._level;
    }

    get primaryClassJob(): JobName {
        return this._classJob;
    }

    get allClassJobs(): JobName[] {
        return [...this._allJobs];
    }

    getIlvlSyncInfo(ilvl: number): IlvlSyncInfo | undefined {
        return this._isyncData.get(ilvl);
    }

    getImplicitIlvlSync(level: number, isWeapon: boolean): number | undefined {
        return (isWeapon ? this._maxIlvlForEquipLevelWeapon : this._maxIlvlForEquipLevel).get(level);
    }
}


function slotValue(raw: number): EquipSlotValue {
    switch (raw) {
        case 0:
            return 'none';
        case 1:
            return 'equip';
        case -1:
            return 'block';
        default:
            console.error("Unknown slot value!", raw);
            return 'none';
    }
}

export class DataApiEquipSlotMap implements EquipSlotMap {

    readonly Weapon: EquipSlotValue;
    readonly OffHand: EquipSlotValue;
    readonly Head: EquipSlotValue;
    readonly Body: EquipSlotValue;
    readonly Hand: EquipSlotValue;
    readonly Legs: EquipSlotValue;
    readonly Feet: EquipSlotValue;
    readonly Ears: EquipSlotValue;
    readonly Neck: EquipSlotValue;
    readonly Wrist: EquipSlotValue;
    readonly RingLeft: EquipSlotValue;
    readonly RingRight: EquipSlotValue;
    readonly displayGearSlotName: DisplayGearSlotKey;
    readonly occGearSlotName: OccGearSlotKey;

    constructor(cat: EquipSlotCategory) {
        this.Weapon = slotValue(cat.mainHand);
        this.OffHand = slotValue(cat.offHand);
        this.Head = slotValue(cat.head);
        this.Body = slotValue(cat.body);
        this.Hand = slotValue(cat.gloves);
        this.Legs = slotValue(cat.legs);
        this.Feet = slotValue(cat.feet);
        this.Ears = slotValue(cat.ears);
        this.Neck = slotValue(cat.neck);
        this.Wrist = slotValue(cat.wrists);
        this.RingLeft = slotValue(cat.fingerL);
        this.RingRight = slotValue(cat.fingerR);
        if (this.canEquipTo('Weapon')) {
            this.displayGearSlotName = 'Weapon';
            if (this.OffHand === 'block') {
                this.occGearSlotName = 'Weapon2H';
            }
            else {
                this.occGearSlotName = 'Weapon1H';
            }
        }
        else if (this.canEquipTo('RingLeft') || this.canEquipTo('RingRight')) {
            this.displayGearSlotName = 'Ring';
            this.occGearSlotName = 'Ring';
        }
        else {
            const normalSlots: (EquipSlotKey & OccGearSlotKey & DisplayGearSlotKey)[] = ['OffHand', 'Head', 'Body', 'Hand', 'Legs', 'Feet', 'Ears', 'Neck', 'Wrist'] as const;
            const hasBlockedSlot = this.getBlockedSlots().length > 0;
            const eq = arrayEqTyped<EquipSlotKey>;
            if (hasBlockedSlot) {
                const all = this.equipOrBlockedSlots;
                if (eq(all, ['Head', 'Body', 'Legs', 'Feet'])) {
                    this.occGearSlotName = 'ChestHeadLegsFeet';
                }
                else if (eq(all, ['Head', 'Body'])) {
                    this.occGearSlotName = 'ChestHead';
                }
                else if (eq(all, ['Body', 'Legs', 'Feet'])) {
                    this.occGearSlotName = 'ChestLegsFeet';
                }
                else if (eq(all, ['Body', 'Hand', 'Legs'])) {
                    this.occGearSlotName = 'ChestLegsGloves';
                }
                else if (eq(all, ['Head', 'Body', 'Hand', 'Legs', 'Feet'])) {
                    this.occGearSlotName = 'HeadChestHandsLegsFeet';
                }
                else if (eq(all, ['Legs', 'Feet'])) {
                    this.occGearSlotName = 'LegsFeet';
                }
            }
            for (const normalSlot of normalSlots) {
                if (this.canEquipTo(normalSlot)) {
                    this.displayGearSlotName = normalSlot;
                    // Set this if the logic above didn't find anything
                    if (!this.occGearSlotName) {
                        this.occGearSlotName = normalSlot;
                    }
                    break;
                }
            }
        }
    }

    canEquipTo(slot: EquipSlotKey): boolean {
        return this[slot] === 'equip';
    }

    get equipOrBlockedSlots(): EquipSlotKey[] {
        return EquipSlots.filter(slot => this[slot] !== 'none');
    }

    getBlockedSlots(): EquipSlotKey[] {
        return EquipSlots.filter(slot => this[slot] === 'block');
    }
}

// noinspection RedundantIfStatementJS
export class DataApiGearInfo implements GearItem {
    readonly id: number;
    readonly name: string;
    readonly nameTranslation: TranslatableString;
    readonly iconUrl: URL;
    readonly equipLvl: number;
    readonly ilvl: number;
    readonly displayGearSlot: DisplayGearSlotInfo;
    readonly displayGearSlotName: DisplayGearSlotKey;
    readonly occGearSlotName: OccGearSlotKey;
    // Base stats, including caps
    private baseStats: RawStats;
    primarySubstat: keyof RawStats | null;
    secondarySubstat: keyof RawStats | null;
    materiaSlots: MateriaSlot[];
    readonly isCustomRelic: boolean;
    readonly isUnique: boolean;
    unsyncedVersion: DataApiGearInfo;
    readonly acquisitionType: GearAcquisitionSource;
    readonly jobs: JobName[];
    statCaps: RawStatsPart;
    isSyncedDown: boolean;
    relicStatModel: RelicStatModel;
    syncedDownTo: number | null;
    isNqVersion: boolean;
    readonly rarity: number;
    readonly specialStatType: SpecialStatType;
    readonly specialStats: RawStats | null;
    // activeSpecialStat: SpecialStatType | null = null;
    private _activeSpecialStat: SpecialStatType | null = null;
    // Actual effective stats
    stats: RawStats;
    readonly slotMapping: DataApiEquipSlotMap;
    readonly meldParamIndex: number;
    readonly quality: ItemQuality;

    constructor(data: ApiItemData, forceNq: boolean = false) {
        this.jobs = data.classJobs as JobName[];
        this.isNqVersion = forceNq;
        this.id = data.rowId;
        if (forceNq) {
            this.name = '(NQ) ' + data.name;
            this.nameTranslation = toTranslatable(data.name, data.nameTranslations, i => `(NQ) ${i}`);
        }
        else {
            this.name = data.name;
            this.nameTranslation = toTranslatable(data.name, data.nameTranslations);
        }
        if (data.canBeHq) {
            this.quality = forceNq ? 'nq' : 'hq';
        }
        else {
            this.quality = 'none';
        }
        this.equipLvl = data.equipLevel;
        this.ilvl = data.ilvl;
        let iconUrl;
        try {
            iconUrl = new URL(data.icon.url);
        }
        catch (e) {
            iconUrl = new URL(xivApiIconUrl(26270));
        }
        this.iconUrl = iconUrl;
        const slotMap = new DataApiEquipSlotMap(data.equipSlotCategory);
        this.displayGearSlotName = slotMap.displayGearSlotName;
        this.occGearSlotName = slotMap.occGearSlotName;
        this.slotMapping = slotMap;
        this.displayGearSlot = this.displayGearSlotName ? DisplayGearSlotMapping[this.displayGearSlotName] : undefined;
        const weaponDelayRaw = (data.delayMs);
        this.baseStats = new RawStats();
        this.baseStats.wdPhys = forceNq ? data.damagePhys : data.damagePhysHQ;
        this.baseStats.wdMag = forceNq ? data.damageMag : data.damageMagHQ;
        this.baseStats.weaponDelay = weaponDelayRaw ? (weaponDelayRaw / 1000.0) : 0;
        this.baseStats.defenseMag = forceNq ? data.defenseMag : data.defenseMagHQ;
        this.baseStats.defensePhys = forceNq ? data.defensePhys : data.defensePhysHQ;
        const paramMap = forceNq ? data.baseParamMap : data.baseParamMapHQ;
        for (const key in paramMap) {
            const intKey = parseInt(key);
            const baseParam = statById(intKey);
            // WD, delay, def are already accounted for
            if (baseParam === undefined || baseParam === 'wdPhys' || baseParam === 'wdMag' || baseParam === 'weaponDelay' || baseParam === 'defenseMag' || baseParam === 'defensePhys') {
                continue;
            }
            // We need to add here, because we don't want to overwrite wdPhys/wdMag/weaponDelay
            this.baseStats[baseParam] += paramMap[key] ?? 0;
        }
        if (data.specialStatType) {
            this.specialStats = new RawStats();
            this.specialStatType = data.specialStatType;
            // Previously, the assumption was that special stats would also be keys of the normal param map, but
            // this is false for things like Bozja/Eureka haste, as haste is never present in the normal param map.
            const allParamKeys = new Set(Object.keys(paramMap));
            for (const baseParamMapSpecialKey in data.baseParamMapSpecial) {
                allParamKeys.add(baseParamMapSpecialKey);
            }
            for (const key of allParamKeys.values()) {
                const intKey = parseInt(key);
                const baseParam = statById(intKey);
                if (baseParam === undefined) {
                    continue;
                }
                // Haste is stored in the game files as a negative, i.e. negative value = faster, but we treat it as a
                // positive, the way it is shown in-game on the UI.
                if (baseParam === 'gearHaste') {
                    this.specialStats[baseParam] = -data.baseParamMapSpecial[key];
                }
                else {
                    this.specialStats[baseParam] = data.baseParamMapSpecial[key];
                }
            }
        }
        else {
            this.specialStats = null;
            this.specialStatType = null;
        }
        this.isUnique = data.unique;
        this.computeSubstats();
        this.materiaSlots = [];
        const baseMatCount: number = data.materiaSlotCount;
        // If there are no materia slots, and it is purple rarity, then it might be a custom relic
        if (baseMatCount === 0 && data.rarity === 4) {
            // Pre-order earrings and other items that directly provide main stat
            if (this.baseStats.extraMainStat) {
                this.isCustomRelic = false;
            }
            else if (!this.primarySubstat) {
                // If there is no primary substat on the item, then consider it a relic
                this.isCustomRelic = true;
            }
            else {
                // Otherwise, it's just a random item that just so happens to not have materia slots
                this.isCustomRelic = false;
            }
        }
        else {
            // If it has materia slots, it definitely isn't a custom relic
            this.isCustomRelic = false;
            // Is overmelding allowed?
            const overmeld: boolean = data.advancedMeldingPermitted;
            // The materia slot count represents slots that are always meldable
            for (let i = 0; i < baseMatCount; i++) {
                // TODO: figure out grade automatically - isn't this filtering done on the UI somewhere?
                this.materiaSlots.push({
                    maxGrade: MATERIA_LEVEL_MAX_NORMAL,
                    allowsHighGrade: true,
                    ilvl: data.ilvl,
                });
            }
            if (overmeld) {
                // If we can also overmeld, then push a *single* high-grade slot, and then the rest are only for
                // small materia.
                this.materiaSlots.push({
                    maxGrade: MATERIA_LEVEL_MAX_NORMAL,
                    allowsHighGrade: true,
                    ilvl: data.ilvl,
                });
                for (let i = this.materiaSlots.length; i < MATERIA_SLOTS_MAX; i++) {
                    this.materiaSlots.push({
                        maxGrade: MATERIA_LEVEL_MAX_OVERMELD,
                        allowsHighGrade: false,
                        ilvl: data.ilvl,
                    });
                }
            }
        }
        const acqSrcRaw: AcqSrc = data.acquisitionSource;
        switch (acqSrcRaw) {
            case AcqSrc.NormalRaid:
                this.acquisitionType = 'normraid';
                break;
            case AcqSrc.SavageRaid:
                this.acquisitionType = 'raid';
                break;
            case AcqSrc.Tome:
                this.acquisitionType = 'tome';
                break;
            case AcqSrc.AugTome:
                this.acquisitionType = 'augtome';
                break;
            case AcqSrc.Crafted:
                this.acquisitionType = 'crafted';
                break;
            case AcqSrc.AugCrafted:
                this.acquisitionType = 'augcrafted';
                break;
            case AcqSrc.Relic:
                this.acquisitionType = 'relic';
                break;
            case AcqSrc.Dungeon:
                this.acquisitionType = 'dungeon';
                break;
            case AcqSrc.ExtremeTrial:
                this.acquisitionType = 'extrial';
                break;
            case AcqSrc.Ultimate:
                this.acquisitionType = 'ultimate';
                break;
            case AcqSrc.Artifact:
                this.acquisitionType = 'artifact';
                break;
            case AcqSrc.AllianceRaid:
                this.acquisitionType = 'alliance';
                break;
            case AcqSrc.Criterion:
                this.acquisitionType = 'criterion';
                break;
            case AcqSrc.DeepDungeon:
                this.acquisitionType = 'deepdungeon';
                break;
            case AcqSrc.FieldOperation:
                this.acquisitionType = 'fieldoperation';
                break;
            case AcqSrc.Other:
                this.acquisitionType = 'other';
                break;
            case AcqSrc.Custom:
                this.acquisitionType = 'custom';
                break;
            case AcqSrc.Unknown:
                this.acquisitionType = 'other';
                break;
            default:
                this.acquisitionType = 'other';
                break;
        }
        this.rarity = data.rarity;
        this.meldParamIndex = data.meldParamIndex;
        this.recalcEffectiveStats();
    }

    private computeSubstats() {
        const baseStats = this.unsyncedVersion?.baseStats ?? this.baseStats;
        const sortedStats = Object.entries({
            crit: baseStats.crit,
            dhit: baseStats.dhit,
            determination: baseStats.determination,
            spellspeed: baseStats.spellspeed,
            skillspeed: baseStats.skillspeed,
            piety: baseStats.piety,
            tenacity: baseStats.tenacity,
        })
            .sort((left, right) => {
                if (left[1] > right[1]) {
                    return 1;
                }
                else if (left[1] < right[1]) {
                    return -1;
                }
                return 0;
            })
            .filter(item => item[1])
            .reverse();
        if (sortedStats.length < 2) {
            if (sortedStats.length === 1) {
                this.primarySubstat = sortedStats[0][0] as keyof RawStats;
                this.secondarySubstat = null;
            }
            else {
                this.primarySubstat = null;
                this.secondarySubstat = null;
            }
        }
        else {
            this.primarySubstat = sortedStats[0][0] as keyof RawStats;
            this.secondarySubstat = sortedStats[1][0] as keyof RawStats;
        }
    }

    applyIlvlData(nativeIlvlInfo: IlvlSyncInfo, syncIlvlInfo?: IlvlSyncInfo, level?: number) {
        const statCapsNative: RawStatsPart = {};
        Object.entries(this.baseStats).forEach(([stat, _]) => {
            const rsk = stat as RawStatKey;
            const cap = nativeIlvlInfo.substatCap(this.occGearSlotName, rsk, this.meldParamIndex);
            if (cap !== null) {
                statCapsNative[rsk] = cap;
            }
        });
        this.statCaps = statCapsNative;
        if (syncIlvlInfo && (syncIlvlInfo.ilvl < this.ilvl || level < this.equipLvl)) {
            this.unsyncedVersion = {
                ...this,
            };
            this.syncedDownTo = syncIlvlInfo.ilvl;
            this.materiaSlots = [];
            const statCapsSync: RawStatsPart = {};
            Object.entries(this.baseStats).forEach(([stat, v]) => {
                const rsk = stat as RawStatKey;
                statCapsSync[rsk] = syncIlvlInfo.substatCap(this.occGearSlotName, rsk, this.meldParamIndex);
            });
            this.baseStats = applyStatCaps(this.baseStats, statCapsSync);
            this.statCaps = statCapsSync;
            this.computeSubstats();
            this.isSyncedDown = true;
        }
        else {
            this.unsyncedVersion = this;
            this.syncedDownTo = null;
            this.isSyncedDown = false;
        }
        this.recalcEffectiveStats();
    }

    usableByJob(job: JobName): boolean {
        return this.jobs.includes(job);
    }

    get activeSpecialStat(): SpecialStatType | null {
        return this._activeSpecialStat;
    }

    set activeSpecialStat(value: SpecialStatType | null) {
        this._activeSpecialStat = value;
        this.recalcEffectiveStats();
    }

    recalcEffectiveStats(): void {
        if (this.specialStatType && this.specialStatType === this._activeSpecialStat) {
            const stats = new RawStats(this.baseStats);
            addStats(stats, this.specialStats);
            this.stats = stats;
        }
        else {
            this.stats = this.baseStats;
        }
    }

}

export class DataApiFoodInfo implements FoodItem {
    bonuses: {
        [K in RawStatKey]?: {
            percentage: number;
            max: number
        }
    } = {};
    iconUrl: URL;
    id: number;
    name: string;
    readonly nameTranslation: TranslatableString;
    ilvl: number;
    primaryStat: RawStatKey | undefined;
    secondaryStat: RawStatKey | undefined;
    // We only deal with HQ food
    readonly quality: ItemQuality = 'hq';

    constructor(data: ApiFoodData) {
        this.id = requireNumber(data.rowId);
        this.name = requireString(data.name);
        this.iconUrl = new URL(data.icon.url);
        this.ilvl = requireNumber(data.levelItem);
        this.nameTranslation = toTranslatable(this.name, data.nameTranslations);
        for (const rawKey in data.bonusesHQ) {
            if (rawKey === '0') {
                continue;
            }
            const actualKey = statById(parseInt(rawKey));
            this.bonuses[actualKey] = data.bonusesHQ[rawKey];
        }
        const sortedStats = Object.entries(this.bonuses).sort((entryA, entryB) => entryB[1].max - entryA[1].max).map(entry => entry[0] as RawStatKey).filter(stat => stat !== 'vitality');
        if (sortedStats.length >= 1) {
            this.primaryStat = sortedStats[0];
        }
        if (sortedStats.length >= 2) {
            this.secondaryStat = sortedStats[1];
        }
    }
}

export class DataApiMedicineInfo implements MedicineItem {
    bonuses: {
        [K in RawStatKey]?: {
            percentage: number;
            max: number
        }
    } = {};
    iconUrl: URL;
    id: number;
    name: string;
    readonly nameTranslation: TranslatableString;
    ilvl: number;
    primaryStat: RawStatKey | undefined;
    secondaryStat: RawStatKey | undefined;
    // We only deal with HQ medicine
    readonly quality: ItemQuality = 'hq';

    constructor(data: ApiMedicineData) {
        this.id = requireNumber(data.rowId);
        this.name = requireString(data.name);
        this.iconUrl = new URL(data.icon.url);
        this.ilvl = requireNumber(data.levelItem);
        this.nameTranslation = toTranslatable(this.name, data.nameTranslations);
        for (const rawKey in data.bonusesHQ) {
            if (rawKey === '0') {
                continue;
            }
            const actualKey = statById(parseInt(rawKey));
            this.bonuses[actualKey] = data.bonusesHQ[rawKey];
        }
        const sortedStats = Object.entries(this.bonuses).sort((entryA, entryB) => entryB[1].max - entryA[1].max).map(entry => entry[0] as RawStatKey).filter(stat => stat !== 'vitality');
        if (sortedStats.length >= 1) {
            this.primaryStat = sortedStats[0];
        }
        if (sortedStats.length >= 2) {
            this.secondaryStat = sortedStats[1];
        }
    }
}

export function processRawMateriaInfo(data: ApiMateriaData): Materia[] {
    const out: Materia[] = [];
    for (let i = 0; i < MATERIA_LEVEL_MAX_NORMAL; i++) {
        const itemData = data.item[i];
        const itemId = itemData.rowId;
        const itemName = itemData.name;
        const stats = new RawStats();
        // TODO: use statById in more places
        const stat = statById(data.baseParam);
        if (!stat || !itemName) {
            continue;
        }
        stats[stat] = data.value[i];
        const grade = (i + 1);
        out.push({
            name: itemName,
            nameTranslation: toTranslatable(itemName, itemData.nameTranslations),
            id: itemId,
            iconUrl: new URL(itemData.icon.url),
            stats: stats,
            primaryStat: stat as MateriaSubstat,
            primaryStatValue: stats[stat],
            materiaGrade: grade,
            isHighGrade: (grade % 2) === 0 && grade >= 6,
            ilvl: itemData.ilvl ?? 0,
            quality: 'none',
        });
    }
    return out;
}
